import React, { useState, useEffect } from 'react';
import { useThemeClasses } from '../lib/themes/utils';
import { Footer } from './common/Footer';

interface Testimonial {
  id: number;
  name: string;
  program: string;
  quote: string;
  rating: number;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Sarah M.",
    program: "Gut Healing Protocol",
    quote: "After years of digestive issues, Hailey's personalized approach finally gave me answers. I feel like a completely different person - energized, clear-headed, and pain-free!",
    rating: 5
  },
  {
    id: 2,
    name: "Jessica T.",
    program: "Hormone Balance Journey",
    quote: "Working with Hailey taught me how to listen to my body. Her cycle-syncing guidance transformed my energy levels and mood throughout the month.",
    rating: 5
  },
  {
    id: 3,
    name: "Maria L.",
    program: "Group Workshop Series",
    quote: "The community support combined with evidence-based practices created the perfect healing environment. I've made lasting friendships and lifestyle changes.",
    rating: 5
  },
  {
    id: 4,
    name: "Emily R.",
    program: "1:1 Consultation",
    quote: "Hailey's holistic approach addressed not just my symptoms but the root causes. Her compassionate guidance made all the difference in my healing journey.",
    rating: 5
  }
];

export const HomePage: React.FC = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const theme = useThemeClasses();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className={`min-h-screen ${theme.backgrounds.gradient}`}>
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background with organic shapes */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-secondary-50">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary-100/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary-100/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          
          {/* Animated botanical SVG elements */}
          <svg className="absolute top-20 left-10 w-24 h-24 text-primary-200 opacity-20 animate-float" viewBox="0 0 100 100">
            <path d="M50 10 Q 20 40 25 70 T 50 90 Q 80 60 75 30 Z" fill="currentColor" />
          </svg>
          <svg className="absolute top-40 right-20 w-32 h-32 text-secondary-200 opacity-20 animate-float-delayed" viewBox="0 0 100 100">
            <path d="M50 20 Q 30 30 20 50 T 30 80 L 50 90 L 70 80 Q 80 60 80 50 T 70 30 Z" fill="currentColor" />
          </svg>
        </div>
        
        <div className="relative z-10">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-24 pb-20">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-primary-100/50 backdrop-blur-sm rounded-full">
                <span className="text-primary-600 text-sm font-medium">Welcome to Your Wellness Journey</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl font-light text-gray-900 mb-6 tracking-tight">
                Work <span className="italic text-primary-600">with</span> your body
                <br />
                <span className="font-normal">to heal naturally</span>
              </h1>
              
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-12">
                Discover personalized wellness resources, join transformative group sessions, 
                or begin your journey with a comprehensive health assessment. 
                Every path is designed to honor your body's innate wisdom.
              </p>

              {/* Primary CTA Button */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a 
                  href="/intake"
                  className={`
                    group inline-flex items-center gap-3 px-8 py-4 
                    ${theme.backgrounds.gradientPrimary} text-white 
                    ${theme.radius.large} font-semibold text-lg
                    hover:scale-105 hover:${theme.shadows.large}
                    transition-all ${theme.animations.duration}
                    ${theme.shadows.medium}
                    relative overflow-hidden
                  `}
                >
                  <div className="relative z-10 flex items-center gap-3">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Start Your Wellness Assessment
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                </a>
                
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span>Secure & confidential • Takes 10-15 minutes</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonial Section */}
      <section className="py-20 bg-gradient-to-b from-white to-primary-50/30">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-light text-gray-900 mb-4">
              Stories of Transformation
            </h2>
            <p className="text-lg text-gray-600">
              Real experiences from our healing community
            </p>
          </div>

          {/* Testimonial Carousel */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-8 md:p-12 relative overflow-hidden">
              <div className="absolute top-0 left-0 text-primary-200 opacity-20">
                <svg className="w-32 h-32" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
              </div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-center mb-6">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                
                <blockquote className="text-center">
                  <p className="text-xl md:text-2xl font-light text-gray-800 mb-6 italic">
                    "{testimonials[currentTestimonial].quote}"
                  </p>
                  <footer>
                    <p className="text-lg font-semibold text-gray-900">{testimonials[currentTestimonial].name}</p>
                    <p className="text-primary-600">{testimonials[currentTestimonial].program}</p>
                  </footer>
                </blockquote>
                
                <div className="flex justify-center gap-2 mt-8">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentTestimonial(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentTestimonial 
                          ? 'bg-primary-500 w-8' 
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                      aria-label={`Go to testimonial ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-light text-gray-900 mb-6">
              Ready to begin your healing journey?
            </h2>
            <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
              Whether you're seeking personalized guidance, community support, or self-paced learning, 
              we have a path that honors your unique needs.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <a 
              href="/intake" 
              className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all text-center border border-primary-100 hover:border-primary-300"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <span className="text-2xl">🌱</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Personalized Healing</h3>
              <p className="text-gray-600 mb-6">Complete your wellness assessment to receive personalized 1:1 support and custom healing protocols</p>
              <span className="inline-flex items-center gap-2 text-primary-600 font-medium">
                Begin Assessment
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </a>
            
            <a 
              href="/community" 
              className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all text-center border border-secondary-100"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <span className="text-2xl">👥</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Community Workshops</h3>
              <p className="text-gray-600 mb-6">Join group sessions and workshops to connect with others on similar healing journeys</p>
              <span className="inline-flex items-center gap-2 text-secondary-600 font-medium">
                View Upcoming Sessions
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </a>
            
            <a 
              href="/catalog" 
              className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all text-center border border-gray-200"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-gray-400 to-gray-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <span className="text-2xl">📚</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Self-Guided Resources</h3>
              <p className="text-gray-600 mb-6">Browse our library of guides, protocols, and educational content for self-paced learning</p>
              <span className="inline-flex items-center gap-2 text-gray-700 font-medium">
                Browse Resources
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </a>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};